<template>
	<div class="cart-summary-container" :class="{ open: isOpen }">
		<div class="cart-summary-content">
			<div class="cart-summary-header">
				<h1>Your Order</h1>
				<div class="cart-summary-subtitle" v-if="showFreeDelivery">
					<span>Free Delivery</span>
					to
					<span>{{ businessName }}</span>
				</div>
				<button class="button" @click="close">
					<i class="sk-icon-times"></i>
				</button>
			</div>
			<slot v-if="cart.length > 0">
				<div class="sk-scrollbar" v-if="residentialCart">
					<CartResidentialItem
						v-for="cartSection in cartSections"
						:key="cartSection.id"
						:menuId="cartSection.menuId"
						:items="cartSection.items"
						:date="cartSection.date"
					/>
					<CheckoutAdditionalInfo v-if="showAdditionalInfo" />
				</div>
				<div class="sk-scrollbar" :style="cartSummaryStyle" v-else>
					<CheckoutItem
						class="cart-checkout-item"
						v-for="item in cart"
						:key="item.uuid"
						:item="item"
						:data-cy="`checkout-item-${item.id}`"
					/>
					<CheckoutAdditionalInfo v-if="showAdditionalInfo" />
				</div>
			</slot>
			<div class="cart-empty" v-else>
				<img src="/images/2k-cart-empty.png" />
				<div>
					<div>Your cart is empty</div>
					<div>Add items to get started</div>
				</div>
			</div>
			<div class="cart-bottom-bar">
				<button
					v-if="!isCheckoutPage || (isCheckoutPage && !isMobile)"
					class="button button-primary checkout-btn"
					@click="checkout"
					data-cy="checkout-button"
				>
					<span>
						{{ cart.length }}
					</span>
					<span>
						{{ buttonText }}
					</span>
					<span>
						{{ cartTotal | currency }}
					</span>
				</button>

				<div class="terms-of-use-and-policy-section" v-if="isCheckout">
					<span>
						By placing your order, you agree to be charged and to our
						<a class="url" :href="tosUrl" target="_blank">Terms of Use</a>
						 and 
						<a class="url" :href="privacyUrl" target="_blank">Privacy Policy.</a>
					</span>
				</div>
				<MenuDeliveryBar v-if="showDeliveryBar" />
			</div>
			<CheckoutOrderBlocker v-if="isCheckoutPage" />
		</div>
	</div>
</template>

<style scoped>
.terms-of-use-and-policy-section {
	margin-top: auto;
	margin-bottom: min(25px, 50%);
	padding: 0 20px;
	font-size: 11px;
	color: var(--sk-grey3);
	text-align: center;
}

.url {
	color: var(--gR-teal-60);
}

.cart-summary-container {
	width: 100%;
	height: 100%;
	position: fixed;
	top: 100%;
	bottom: 0;
	transition: top 0.5s ease-in-out;
	z-index: 5000;
}

.cart-summary-content {
	height: 100%;
	overflow-y: auto;
	overflow-x: hidden;
	background-color: var(--sk-grey-bg);
	display: flex;
	flex-direction: column;
}

.cart-summary-container.open {
	top: 0;
}

.cart-summary-header {
	border-bottom: 1px solid var(--gR-white2);
	padding: 20px;
	background-color: var(--gR-white);
}

h1 {
	color: var(--gR-base-80);
	font-size: 18px;
	font-weight: 600;
	margin-bottom: 0;
}

@media only screen and (max-width: 750 px) {
	h1 {
		font-size: 16px;
	}
}

.cart-summary-subtitle {
	color: var(--sk-grey3);
	font-size: 12px;
	margin-top: 10px;
}

.cart-summary-subtitle > span {
	color: var(--gR-base-80);
	font-weight: 600;
}

.cart-summary-header .button {
	width: auto;
	color: var(--sk-grey3);
	background-color: var(--gR-white);
	padding: 4px;
	height: 2rem;
	width: 2rem;
	border-radius: 50%;
	position: absolute;
	top: 5px;
	right: 5px;
}

.cart-summary-header i {
	font-size: 16px;
	color: var(--sk-grey2);
}

.cart-empty {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	flex: 1;
}

.cart-empty img {
	width: 175px;
}

.cart-empty > div {
	margin-top: 45px;
	text-align: center;
}

.cart-empty div {
	color: var(--sk-grey2);
	font-weight: 600;
	font-size: 12px;
}

.cart-checkout-item {
	border-bottom: 1px solid var(--gR-white2);
	flex-shrink: 0;
}

.sk-scrollbar {
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	height: 100%;
}

.sk-scrollbar >>> .checkout-additional-info {
	margin-top: auto;
	margin-bottom: min(25px, 50%);
}

.cart-bottom-bar {
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
}

@media only screen and (min-width: 900px) {
	.cart-summary-container,
	.cart-summary-container.open {
		position: relative;
		width: 375px;
		height: 100%;
		top: 0;
		flex-shrink: 0;
		z-index: 0;
		transition: none;
	}
	.cart-summary-container.open {
		z-index: 1;
	}

	.cart-summary-content {
		background: var(--gR-white);
		box-shadow: 0px 1px 2px rgba(136, 136, 136, 0.25);
		border-radius: 5px;
		margin-top: 20px;
		height: calc(100% - 20px - 20px);
		margin-right: 25px;
		position: relative;
	}

	.cart-summary-header .button {
		display: none;
	}
}
</style>

<script>
import { MenuType } from "@reef-os/utils/enums/menuType"
import cartSummaryMixin from "@/mixins/cart-summary-mixin"
import mobileResponsivenessMixin from "@/mixins/mobile-responsiveness-mixin"
import CartResidentialItem from "@/components/common/CartResidentialItem.vue"
import CheckoutItem from "@/components/checkout/CheckoutItem.vue"
import CheckoutAdditionalInfo from "@/components/checkout/CheckoutAdditionalInfo.vue"
import CheckoutOrderBlocker from "@/components/checkout/CheckoutOrderBlocker.vue"
import MenuDeliveryBar from "@/components/menu/MenuDeliveryBar.vue"

export default {
	name: "FloatingCartSummary",
	mixins: [cartSummaryMixin, mobileResponsivenessMixin],
	props: {
		showDeliveryBar: Boolean
	},
	components: {
		CartResidentialItem,
		CheckoutItem,
		CheckoutAdditionalInfo,
		CheckoutOrderBlocker,
		MenuDeliveryBar
	},
	data: function () {
		return {
			mobileThresholdWidth: 900
		}
	},
	computed: {
		business() {
			return this.$store.state.business
				? this.$store.state.business
				: null
		},
		businessName() {
			return this.business ? this.business.name : ""
		},
		showFreeDelivery() {
			if (this.$store.getters.isAirport) {
				return false
			}
			return (
				this.$store.getters.showCartDeliveryFee &
				(this.$store.getters.deliveryFee <= 0)
			)
		},
		isCheckout(){
			return window.location.href.indexOf('checkout') > -1
		},
		residentialCart() {
			const hasEventProduct = this.$store.state.cart.some(
				(product) => product.menu_type == MenuType.POPUP
			)
			return this.isResidential && !hasEventProduct
		},
		cartSummaryStyle() {
			const offset = this.showDeliveryBar ? "215px" : "160px"
			return {
				height: `calc(100% - ${offset})`
			}
		},
		showAdditionalInfo() {
			if (this.$store.getters.isAirport) {
				return true
			}
			return this.isCheckoutPage && !this.isMobile
		},
		tosUrl() {
			return "/" + this.$route.params.slug + "/terms_of_use"
		},
		privacyUrl() {
			return "/" + this.$route.params.slug + "/privacy"
		}
	},
	watch: {
		isMobile(mobile) {
			if (!mobile) {
				this.isOpen = false
			}
		}
	}
}
</script>